/**
 * Password generation utility for creating secure passwords
 */


// Expanded list of common passwords to avoid
export const COMMON_PASSWORDS = [
  "password", "123456", "password123", "admin", "qwerty", "letmein", "welcome",
  "monkey", "1234567890", "abc123", "111111", "123123", "password1", "1234",
  "12345", "dragon", "master", "hello", "login", "guest", "admin123", "root",
  "test", "user", "super", "secret", "football", "baseball", "michael", "jordan",
  "harley", "ranger", "corvette", "mustang", "access", "shadow", "master",
  "matrix", "superman", "batman", "trustno1", "passw0rd", "mustang", "ranger",
  "jordan", "jennifer", "hunter", "fuckyou", "2000", "test", "batman", "trustno1",
  "thomas", "robert", "access", "loveme", "buster", "1234567", "soccer",
  "hockey", "killer", "george", "sexy", "andrew", "charlie", "superman",
  "asshole", "fuckyou", "dallas", "jessica", "panties", "pepper", "1111",
  "austin", "william", "daniel", "golfer", "summer", "heather", "hammer",
  "yankees", "joshua", "maggie", "biteme", "enter", "ashley", "thunder",
  "cowboy", "silver", "richard", "fucker", "orange", "merlin", "michelle",
  "corvette", "bigdog", "cheese", "matthew", "patrick", "martin", "freedom",
  "ginger", "blowjob", "nicole", "sparky", "yellow", "camaro", "secret",
  "dick", "falcon", "taylor", "birdman", "donald", "murphy", "mexico",
  "black", "mustang", "brian", "diablo", "pookie", "austin", "anthony"
];

// Character sets for password generation
const UPPERCASE_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWERCASE_LETTERS = 'abcdefghijklmnopqrstuvwxyz';
const NUMBERS = '0123456789';
const SPECIAL_CHARS = '!@#$%^&*()_+-=[]{}|;:,.<>?';

/**
 * Generates a cryptographically secure random password
 * @param length - Length of the password (default: 16)
 * @param includeUppercase - Include uppercase letters (default: true)
 * @param includeLowercase - Include lowercase letters (default: true)
 * @param includeNumbers - Include numbers (default: true)
 * @param includeSpecialChars - Include special characters (default: true)
 * @returns Generated password string
 */
export function generateSecurePassword(
  length: number = 16,
  includeUppercase: boolean = true,
  includeLowercase: boolean = true,
  includeNumbers: boolean = true,
  includeSpecialChars: boolean = true
): string {
  let charset = '';
  let requiredChars = '';

  if (includeUppercase) {
    charset += UPPERCASE_LETTERS;
    requiredChars += getRandomChar(UPPERCASE_LETTERS);
  }
  
  if (includeLowercase) {
    charset += LOWERCASE_LETTERS;
    requiredChars += getRandomChar(LOWERCASE_LETTERS);
  }
  
  if (includeNumbers) {
    charset += NUMBERS;
    requiredChars += getRandomChar(NUMBERS);
  }
  
  if (includeSpecialChars) {
    charset += SPECIAL_CHARS;
    requiredChars += getRandomChar(SPECIAL_CHARS);
  }

  if (charset === '') {
    throw new Error('At least one character type must be selected');
  }

  let password = '';
  let attempts = 0;
  const maxAttempts = 50;

  do {
    password = requiredChars;
    
    // Fill the rest of the password with random characters
    for (let i = requiredChars.length; i < length; i++) {
      password += getRandomChar(charset);
    }

    // Shuffle the password to randomize the position of required characters
    password = shuffleString(password);
    attempts++;
    
    // Prevent infinite loop in case of edge cases
    if (attempts > maxAttempts) {
      break;
    }
  } while (isPasswordCommon(password) || !isPasswordStrong(password));

  return password;
}

/**
 * Gets a cryptographically secure random character from a charset
 */
function getRandomChar(charset: string): string {
  const array = new Uint32Array(1);
  let randomValue;
  
  do {
    crypto.getRandomValues(array);
    randomValue = array[0];
  } while (randomValue >= Math.floor(0x100000000 / charset.length) * charset.length);
  
  return charset[randomValue % charset.length];
}

/**
 * Shuffles a string using Fisher-Yates algorithm with crypto random
 */
function shuffleString(str: string): string {
  const array = str.split('');
  
  for (let i = array.length - 1; i > 0; i--) {
    const randomArray = new Uint32Array(1);
    crypto.getRandomValues(randomArray);
    const j = randomArray[0] % (i + 1);
    [array[i], array[j]] = [array[j], array[i]];
  }
  
  return array.join('');
}

/**
 * Checks if a password is in the common passwords list
 */
export function isPasswordCommon(password: string): boolean {
  return COMMON_PASSWORDS.includes(password.toLowerCase());
}

/**
 * Validates password strength requirements (basic validation for password generation)
 */
export function isPasswordStrong(password: string): boolean {
  const basicRequirements = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    numbers: /\d/.test(password),
    symbols: /[^A-Za-z0-9]/.test(password),
    notCommon: !isPasswordCommon(password),
    noСyrillic: !/[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F]/.test(password)
  };

  // Use basic validation for password generation (Enzoic validation happens in the UI)
  return Object.values(basicRequirements).every(Boolean);
}

/**
 * Generates a password and ensures it meets all requirements including Enzoic validation
 */
export function generatePasswordForSignup(): string {
  let password = generateSecurePassword(16, true, true, true, true);
  let attempts = 0;
  const maxAttempts = 25; // Increased attempts for Enzoic validation

  // Ensure the generated password meets all requirements including Enzoic
  while (!isPasswordStrong(password) && attempts < maxAttempts) {
    // Try different lengths and complexity for better Enzoic scores
    const length = 14 + (attempts % 6); // Vary length from 14-19
    password = generateSecurePassword(length, true, true, true, true);
    attempts++;
  }

  // If we still don't have a strong password, generate a more complex one
  if (!isPasswordStrong(password)) {
    password = generateComplexPassword();
  }

  return password;
}

/**
 * Generates a highly complex password designed to pass Enzoic validation
 */
function generateComplexPassword(): string {
  const parts = [
    getRandomChar(UPPERCASE_LETTERS) + getRandomChar(UPPERCASE_LETTERS),
    getRandomChar(LOWERCASE_LETTERS) + getRandomChar(LOWERCASE_LETTERS) + getRandomChar(LOWERCASE_LETTERS),
    getRandomChar(NUMBERS) + getRandomChar(NUMBERS),
    getRandomChar(SPECIAL_CHARS) + getRandomChar(SPECIAL_CHARS),
    getRandomChar(LOWERCASE_LETTERS) + getRandomChar(NUMBERS) + getRandomChar(SPECIAL_CHARS),
    getRandomChar(UPPERCASE_LETTERS) + getRandomChar(LOWERCASE_LETTERS) + getRandomChar(NUMBERS)
  ];
  
  return shuffleString(parts.join(''));
}

/**
 * Copies text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand('copy');
      textArea.remove();
      return result;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}
